// src/components/TextEditor.js
import React, { useState, useRef, useEffect } from 'react';
import { Bold, Save, X, List, Type, ListOrdered, Copy } from 'lucide-react';
import { convertHtmlToMarkdown, convertMarkdownToHtml } from './markdownConverter';

const TextEditor = ({ 
  initialContent = '', 
  onSave, 
  onCancel,
  title,
  height = 'h-full'
}) => {
  const [content, setContent] = useState(initialContent);
  const [isEditing, setIsEditing] = useState(false);
  const editorRef = useRef(null);

  // When not in editing mode, sync the internal state with the initialContent prop.
  // This ensures that external changes are reflected.
  useEffect(() => {
    if (!isEditing) {
      setContent(initialContent);
    }
  }, [initialContent, isEditing]);

  // When editing starts, focus the editor. We no longer need to manually
  // set its innerHTML, as it already contains the correct content from the
  // last render, which prevents the cursor from jumping.
  useEffect(() => {
    if (isEditing && editorRef.current) {
      editorRef.current.focus();
    }
  }, [isEditing]);

  // Add event listener for custom markdown copy functionality.
  useEffect(() => {
    const handleCopy = (e) => {
      if (isEditing && editorRef.current && window.getSelection().toString().length > 0) {
        e.preventDefault();
        
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const fragment = range.cloneContents();
          const div = document.createElement('div');
          div.appendChild(fragment.cloneNode(true));
          
          const markdownContent = convertHtmlToMarkdown(div.innerHTML);
          e.clipboardData.setData('text/plain', markdownContent);
        }
      }
    };

    const editorNode = editorRef.current;
    if (isEditing && editorNode) {
      editorNode.addEventListener('copy', handleCopy);
      return () => editorNode.removeEventListener('copy', handleCopy);
    }
  }, [isEditing]);

  const handleCopyMarkdown = async () => {
    if (editorRef.current) {
      const markdownContent = convertHtmlToMarkdown(editorRef.current.innerHTML);
      try {
        await navigator.clipboard.writeText(markdownContent);
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }
    }
  };

  const handleKeyCommand = (e) => {
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          document.execCommand('bold', false);
          break;
        case 's':
          e.preventDefault();
          handleSave();
          break;
        case '.':
          e.preventDefault();
          toggleBulletList();
          break;
        case '1':
          e.preventDefault();
          toggleOrderedList();
          break;
        default:
          break;
      }
    } else if (e.key === 'Enter' && !e.shiftKey) {
      // Improved 'Enter' key logic for a better UX.
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      const currentNode = range.startContainer;
      const blockNode = currentNode.nodeType === 1 ? currentNode : currentNode.parentElement;
      
      const heading = blockNode.closest('h1, h2, h3, h4, h5, h6');
      const listItem = blockNode.closest('li');

      // If at the end of a heading, create a paragraph below it instead of a new heading.
      if (heading && range.collapsed && range.startOffset >= (heading.textContent || '').length) {
        e.preventDefault();
        document.execCommand('insertParagraph', false);
        // Ensure the new block is a paragraph, as `insertParagraph` can be inconsistent.
        document.execCommand('formatBlock', false, 'p');
      } 
      // If in an empty list item, outdent to exit the list.
      else if (listItem && !listItem.textContent.trim()) {
        e.preventDefault();
        document.execCommand('outdent', false);
      }
      // For all other cases, allow the default browser behavior, which is more reliable
      // than the previous implementation that used a non-standard 'insertListItem' command.
    }
  };

  const handleSave = () => {
    if (editorRef.current) {
      const htmlContent = editorRef.current.innerHTML;
      
      // Sanitize the HTML by converting to Markdown and back. This ensures
      // consistent formatting for the LLM.
      const markdownContent = convertHtmlToMarkdown(htmlContent);
      const processedHtmlContent = convertMarkdownToHtml(markdownContent);
      
      setContent(processedHtmlContent);
      onSave(processedHtmlContent);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    // Simply exit editing mode. The content will revert automatically on re-render
    // because the 'content' state was never updated.
    setIsEditing(false);
    onCancel?.();
  };
  
  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const executeCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const toggleBold = () => executeCommand('bold');
  const toggleBulletList = () => executeCommand('insertUnorderedList');
  const toggleOrderedList = () => executeCommand('insertOrderedList');
  const applyH4 = () => executeCommand('formatBlock', 'h4');

  const toolbarButtonClass = "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-950 disabled:pointer-events-none disabled:opacity-50 hover:bg-gray-700 hover:text-gray-100 h-9 w-9 text-gray-900 [&.bg-gray-700]:text-gray-100";
  
  return (
    <div className={`flex flex-col ${height} bg-white rounded-lg shadow-sm`}>
      <div className="flex justify-between items-center px-4 py-2 bg-white border-b border-gray-200 rounded-t-lg">
        <h2 className={`text-base font-semibold text-gray-800 ${isEditing ? 'hidden sm:block' : 'block'}`}>{title}</h2>
        {isEditing ? (
          <div className="flex items-center gap-1 w-full sm:w-auto justify-end">
            <div className="flex items-center gap-1 border-r border-gray-500 pr-2 mr-2">
              <button onClick={toggleBold} className={toolbarButtonClass} title="Bold (Ctrl+B)">
                <Bold className="h-4 w-4" />
              </button>
              <button onClick={applyH4} className={toolbarButtonClass} title="Heading 4">
                <Type className="h-4 w-4" />
              </button>
              <button onClick={toggleBulletList} className={toolbarButtonClass} title="Bullet List (Ctrl+.)">
                <List className="h-4 w-4" />
              </button>
              <button onClick={toggleOrderedList} className={toolbarButtonClass} title="Numbered List (Ctrl+1)">
                <ListOrdered className="h-4 w-4" />
              </button>
              <button onClick={handleCopyMarkdown} className={toolbarButtonClass} title="Copy as Markdown">
                <Copy className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={handleSave}
              className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-950 disabled:pointer-events-none disabled:opacity-50 bg-primary hover:bg-opacity-90 h-9 px-4 text-white"
              title="Save (Ctrl+S)"
            >
              <Save className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Save</span>
            </button>
            <button
              onClick={handleCancel}
              className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-950 disabled:pointer-events-none disabled:opacity-50 bg-text hover:bg-opacity-90 h-9 px-4 text-white"
              title="Cancel (Esc)"
            >
              <X className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Cancel</span>
            </button>
          </div>
        ) : (
          <button
            onClick={() => setIsEditing(true)}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-950 disabled:pointer-events-none disabled:opacity-50 bg-gray-100 hover:bg-gray-200 h-9 px-4 text-gray-800"
          >
            Edit
          </button>
        )}
      </div>
      <div 
        ref={editorRef}
        contentEditable={isEditing}
        onKeyDown={handleKeyCommand}
        onDoubleClick={handleDoubleClick}
        className={`flex-1 overflow-y-auto p-4 font-mono text-sm whitespace-pre-wrap leading-relaxed bg-white ${
          isEditing ? 'outline-none ring-1 ring-blue-100 focus:ring-2 focus:ring-blue-200' : ''
        } [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:pl-1.5 [&_p]:m-0
          [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-6 [&_h1]:mb-2
          [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-5 [&_h2]:mb-1.5
          [&_h3]:text-lg [&_h3]:font-bold [&_h3]:mt-4 [&_h3]:mb-1.5
          [&_h4]:text-base [&_h4]:font-bold [&_h4]:mt-4 [&_h4]:mb-1
          [&_h5]:text-sm [&_h5]:font-bold [&_h5]:mt-3 [&_h5]:mb-1
          [&_h6]:text-xs [&_h6]:font-bold [&_h6]:mt-2 [&_h6]:mb-0.5
          [&_ol]:[list-style-type:decimal] [&_ol>li]:[display:list-item]`}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    </div>
  );
};

export default TextEditor;